import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { adminGet, adminPost } from './adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const FEATURE_META = {
  temperature: { label: 'Temperature', settings: [['min_c', 'min °C'], ['max_c', 'max °C']] },
  humidity: { label: 'Humidity', settings: [['min_pct', 'min %'], ['max_pct', 'max %']] },
  gps: { label: 'GPS tracking and km', settings: [] },
  bag_events: { label: 'Bag open / close events', settings: [] },
  weight_delta: { label: 'Weight delta', settings: [['alert_above_kg', 'alert above kg']] },
  geofence_lock: { label: 'Geofence lock (lockbox)', settings: [] },
};

const DEFAULT_SETTINGS = { temperature: { min_c: 2, max_c: 8 }, humidity: { min_pct: 30, max_pct: 70 }, weight_delta: { alert_above_kg: 0.5 } };

export default function NewCustomerFormPage() {
  const { verticalCode } = useParams();
  const navigate = useNavigate();
  const [vertical, setVertical] = useState(null);
  const [form, setForm] = useState({
    legal_name: '', gstin: '', drug_licence: '', billing_address: '',
    contact_person: '', mobile: '', email: '', customer_type_code: '',
    admin_email: '', ip_allowlist: '', seat_limit: 25,
    first_admin_name: '', first_admin_mobile: '', first_admin_email: '',
  });
  const { me } = useOutletContext() || {};
  const podName = me?.pod_name || 'shared';
  const [features, setFeatures] = useState({});
  const [passportCert, setPassportCert] = useState('LOGISTICS_ONLY');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminGet('/verticals').then(vs => {
      const v = vs.find(x => x.code === verticalCode);
      if (!v) { toast.error('Unknown vertical'); navigate('/admin/tenants/new'); return; }
      setVertical(v);
      const f = {};
      Object.keys(FEATURE_META).forEach(k => {
        f[k] = { enabled: (v.default_features || []).includes(k), settings: { ...(DEFAULT_SETTINGS[k] || {}) } };
      });
      setFeatures(f);
      setForm(fm => ({ ...fm, customer_type_code: v.customer_types[0]?.code || '' }));
    }).catch(e => toast.error(e.message));
  }, [verticalCode, navigate]);

  const hasPassport = useMemo(() => (vertical?.default_features || []).includes('passport'), [vertical]);
  const showDL = verticalCode === 'PHARMA' || verticalCode === 'VACCINES';

  const save = async (goAllocate) => {
    if (!form.legal_name.trim()) { toast.error('Legal name is required'); return; }
    if (form.first_admin_name.trim().length < 2 || form.first_admin_mobile.trim().length < 8) { toast.error('First admin user: name and mobile are required'); return; }
    setBusy(true);
    try {
      const featureList = Object.entries(features)
        .filter(([, v]) => v.enabled)
        .map(([k, v]) => ({ feature: k, enabled: true, settings: v.settings }));
      if (hasPassport) featureList.push({ feature: 'passport', enabled: true, settings: { certificate_type: passportCert } });
      const res = await adminPost('/customers', {
        legal_name: form.legal_name, vertical_code: verticalCode,
        customer_type_code: form.customer_type_code || null,
        gstin: form.gstin || null, drug_licence: form.drug_licence || null,
        billing_address: form.billing_address || null, contact_person: form.contact_person || null,
        mobile: form.mobile || null, email: form.email || null,
        seat_limit: Number(form.seat_limit) || 25, features: featureList,
        first_admin: { name: form.first_admin_name.trim(), mobile: form.first_admin_mobile.trim(), email: form.first_admin_email.trim() || null, role: 'tenant_admin' },
        ip_allowlist: form.ip_allowlist.split(',').map(s => s.trim()).filter(Boolean),
      });
      const created = res.customer || res;
      toast.success(res.approval ? 'Tenant submitted — waits for a second person (Approvals)' : `Tenant created · first admin logs in by OTP on ${form.first_admin_mobile}`);
      navigate(goAllocate ? `/admin/allocation?customer=${created.id}` : '/admin/tenants');
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  if (!vertical) return <div className="p-8" data-testid="new-customer-loading">Loading…</div>;

  const field = (label, key, props = {}) => (
    <div>
      <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>{label}</label>
      <Input data-testid={`customer-${key}-input`} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })}
        className="bg-white border-[#D9D6CD]" {...props} />
    </div>
  );

  const Card = ({ title, children }) => (
    <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }}>
      <h2 className="text-lg font-bold mb-4" style={{ color: '#1B1D1C' }}>{title}</h2>
      {children}
    </div>
  );

  return (
    <div className="p-8 max-w-6xl" data-testid="new-customer-form-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-4xl font-bold" style={{ color: '#1B1D1C' }}>New tenant <span className="text-lg font-normal" style={{ color: '#9a9e96' }}>· {vertical.name}</span></h1>
        <div className="flex gap-3">
          <Button variant="outline" disabled={busy} data-testid="save-draft-customer-btn" onClick={() => save(false)}
            className="bg-white border-[#D9D6CD]">Save draft</Button>
          <Button disabled={busy} data-testid="save-allocate-customer-btn" onClick={() => save(true)}
            className="text-white hover:opacity-90" style={{ background: '#0B6B55' }}>Save and allocate devices</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-5">
          <Card title="1 · Company details">
            <div className="space-y-3">
              {field('Legal name', 'legal_name', { placeholder: '[Company name]' })}
              <div className="grid grid-cols-2 gap-3">
                {field('GSTIN', 'gstin', { placeholder: '[GSTIN]' })}
                {showDL ? field('Drug licence no.', 'drug_licence', { placeholder: '[DL no.]' }) : <div />}
              </div>
              {field('Billing address', 'billing_address', { placeholder: '[Address]' })}
              <div className="grid grid-cols-3 gap-3">
                {field('Contact person', 'contact_person', { placeholder: '[Name]' })}
                {field('Mobile', 'mobile', { placeholder: '[Mobile]' })}
                {field('Email', 'email', { placeholder: '[Email]' })}
              </div>
            </div>
          </Card>

          <Card title="2 · Customer type">
            <div className="flex gap-2 flex-wrap">
              {vertical.customer_types.map(t => (
                <button key={t.code} data-testid={`customer-type-${t.code}`} onClick={() => setForm({ ...form, customer_type_code: t.code })}
                  className="px-4 py-1.5 rounded-full text-sm border transition-colors"
                  style={form.customer_type_code === t.code
                    ? { background: '#E7F2EE', borderColor: '#0B6B55', color: '#0B6B55' }
                    : { background: 'white', borderColor: '#D9D6CD', color: '#4b504a' }}>
                  {t.name}
                </button>
              ))}
            </div>
            <p className="text-xs mt-3" style={{ color: '#9a9e96' }}>Type pre-fills a default feature bundle and alert limits; you can still change them.</p>
          </Card>
        </div>

        <div className="space-y-5">
          <Card title="3 · Feature bundle">
            {Object.entries(FEATURE_META).map(([k, meta]) => (
              <div key={k} className="flex items-center justify-between py-2.5 border-b" style={{ borderColor: '#F3F1EA' }}>
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="font-medium">{meta.label}</span>
                  {features[k]?.enabled && meta.settings.map(([sk, sl]) => (
                    <span key={sk} className="flex items-center gap-1 text-xs" style={{ color: '#6b7069' }}>
                      {sl}
                      <input data-testid={`feature-${k}-${sk}`} type="number" value={features[k].settings[sk] ?? ''}
                        onChange={e => setFeatures({ ...features, [k]: { ...features[k], settings: { ...features[k].settings, [sk]: Number(e.target.value) } } })}
                        className="w-16 h-6 px-1 rounded border text-xs" style={{ borderColor: '#D9D6CD' }} />
                    </span>
                  ))}
                </div>
                <input type="checkbox" data-testid={`feature-toggle-${k}`} checked={!!features[k]?.enabled}
                  onChange={e => setFeatures({ ...features, [k]: { ...features[k], enabled: e.target.checked } })}
                  className="w-4 h-4 accent-[#0B6B55]" />
              </div>
            ))}
            {hasPassport && (
              <div className="flex items-center justify-between pt-3">
                <span className="text-sm font-medium">Passport certificate</span>
                <select data-testid="passport-cert-select" value={passportCert} onChange={e => setPassportCert(e.target.value)}
                  className="h-9 px-3 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
                  <option value="LOGISTICS_ONLY">Logistics only (no product data)</option>
                  <option value="PRODUCT_AND_LOGISTICS">Product and logistics</option>
                </select>
              </div>
            )}
          </Card>

          <Card title="4 · Portal access">
            <div className="grid grid-cols-2 gap-3">
              {field('Office IP allowlist', 'ip_allowlist', { placeholder: '[IP / range, comma separated]' })}
              {field('Seat limit', 'seat_limit', { type: 'number' })}
            </div>
            <p className="text-xs mt-3" style={{ color: '#9a9e96' }}>IP allowlist applies to office roles only; field staff log in on mobile data. Pod: <span className="font-mono">{podName}</span>.</p>
            {verticalCode === 'BFSI' && podName !== 'bfsi' && (
              <p className="text-xs mt-2 px-3 py-2 rounded" style={{ background: '#FBF3DE', color: '#7A5C13' }} data-testid="bfsi-shared-pod-note">
                BFSI tenants should be registered on the BFSI pod. Creating it here is allowed and recorded in the audit log ("BFSI tenant created on shared pod").
              </p>
            )}
          </Card>

          <Card title="5 · First admin user (required)">
            <div className="grid grid-cols-3 gap-3">
              {field('Name', 'first_admin_name', { placeholder: '[Full name]' })}
              {field('Mobile', 'first_admin_mobile', { placeholder: '+91…' })}
              {field('Email (optional)', 'first_admin_email', { placeholder: '[Email]' })}
            </div>
            <p className="text-xs mt-3" style={{ color: '#6b7069' }}>Role: <span className="font-medium">Tenant admin</span>. No password is set — the user signs in with a one-time code sent to this mobile (or email) on first use. A tenant is never created without this user.</p>
          </Card>
        </div>
      </div>
    </div>
  );
}
